// === Task 10 ===
// Inline substep shown while a workflow-architect generation job is in flight.
// Ported from _editor/components/workflow/GenerationModal.jsx — the modal
// chrome (backdrop, role="dialog", scss) is dropped; the parent (SuggestionsTab)
// renders this conditionally as a substep panel.
//
// Subscribes to a per-job socket room (`job_<jobId>`) via a window-level
// CustomEvent bridge. The studio's higher-level useJobSocket subscription
// forwards `job_progress_update` / `job_completed` / `job_failed` events from
// JobManagerService into this CustomEvent shape:
//
//   window.dispatchEvent(new CustomEvent("workflow-architect-job", {
//     detail: { type: "progress" | "completed" | "failed", payload: { jobId, ... } }
//   }))
//
// Both useJobSocket (production socket path) and the window listener
// (test-driven CustomEvents + JobWidgetHost JOB_APPROVED bridge) are kept so a
// single state machine handles every source.
//
// When the user clicks Cancel mid-job (status === "running"), the job keeps
// running — the job widget integration ensures the running job continues to
// surface via the per-user socket room used by the widget.
import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { useJobSocket } from "../../../../utils/jobSocket";
import "./GenerationProgress.scss";

function GenerationProgress({ jobId, onClose, onSuccess }) {
  const [status, setStatus] = useState("running");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // Reset transient state every time we mount for a new job.
  useEffect(() => {
    if (jobId) {
      setStatus("running");
      setProgress(0);
      setError(null);
    }
  }, [jobId]);

  // Direct socket subscription to the per-job room. Translates
  // JobManagerService events into the same CustomEvent shape consumed by the
  // window-level bridge below — this keeps a single code path for handling
  // progress/completed/failed regardless of source (live socket vs. test
  // CustomEvent vs. JobWidgetHost JOB_APPROVED bridge).
  useJobSocket(jobId, (event) => {
    if (!event || event.jobId !== jobId) return;
    const ev = event._event;
    let type = null;
    if (ev === "job_progress_update") type = "progress";
    else if (ev === "job_completed") type = "completed";
    else if (ev === "job_failed") type = "failed";
    if (!type) return;
    window.dispatchEvent(
      new CustomEvent("workflow-architect-job", {
        detail: {
          type,
          payload: {
            jobId,
            percent: event.progress,
            result: event.result,
            error: event.error,
          },
        },
      })
    );
  });

  useEffect(() => {
    if (!jobId) return undefined;

    const onJobEvent = (e) => {
      const detail = e && e.detail;
      if (!detail) return;
      const { type, payload } = detail;
      if (!payload || payload.jobId !== jobId) return;
      if (type === "progress") {
        const pct = typeof payload.percent === "number" ? payload.percent : payload.progress;
        if (typeof pct === "number") setProgress(pct);
      }
      if (type === "completed") {
        setStatus("done");
        setProgress(100);
        if (onSuccess) onSuccess(payload.result);
      }
      if (type === "failed") {
        setStatus("failed");
        setError(payload.error || "generation failed");
      }
    };
    window.addEventListener("workflow-architect-job", onJobEvent);
    return () => window.removeEventListener("workflow-architect-job", onJobEvent);
  }, [jobId, onSuccess]);

  return (
    <div className="generation-progress">
      <div className="generation-progress__body">
        <div className="generation-progress__title">
          {status === "running" &&
            i18n.t("Workflow.generating", { defaultValue: "Generating workflow…" })}
          {status === "done" && i18n.t("Workflow.done", { defaultValue: "Done" })}
          {status === "failed" && i18n.t("Workflow.failed", { defaultValue: "Generation failed" })}
        </div>
        {status === "running" && (
          <div className="generation-progress__progress">
            <div
              className="generation-progress__bar"
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
            />
          </div>
        )}
        {status === "failed" && error && (
          <div className="generation-progress__error">{String(error)}</div>
        )}
        <div className="generation-progress__actions">
          {status === "running" && (
            // Cancel here only dismisses the substep — the job keeps running
            // and continues to surface via the job widget (per-user socket room).
            <button type="button" onClick={onClose} className="generation-progress__close-running">
              {i18n.t("Workflow.cancel", { defaultValue: "Cancel" })}
            </button>
          )}
          {status !== "running" && (
            <button type="button" onClick={onClose} className="generation-progress__close-done">
              {i18n.t("Workflow.close", { defaultValue: "Close" })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerationProgress;
// === /Task 10 ===
